import React from 'react';
import { useApolloClient, } from '@apollo/client';
import { CachePersistor } from 'apollo3-cache-persist';
import IDBStorageProvider from './IDBStorageProvider';
const SCHEMA_VERSION = '10';
const SCHEMA_VERSION_KEY = 'apollo-schema-version';
export const LEGACY_CACHE_KEY = 'apollo-cache-persist';
/** Storage key for one identity's persisted cache. */
export const cacheKeyForIdentity = (identity) => `${LEGACY_CACHE_KEY}:${identity}`;
// The caller only needs to provide a compatible `cache` object, which avoids
// exporting an exact ApolloClient type from this package boundary.
export const getCachePersistor = (client, identity) => {
    const persistor = new CachePersistor({
        cache: client.cache,
        storage: IDBStorageProvider,
        maxSize: false,
        key: cacheKeyForIdentity(identity),
    });
    return persistor;
};
export const Rehydrated = ({ identity, children, fallback = React.createElement("span", null, "Loading offline cache..."), }) => {
    const [restoredFor, setRestoredFor] = React.useState(undefined);
    const client = useApolloClient();
    const persistorRef = React.useRef(null);
    React.useEffect(() => {
        if (restoredFor === identity)
            return;
        // cancelled flag patterned suggested instead of isMounted, to avoid race conditions and setState on unmounted components
        // https://react.dev/reference/react/useEffect#fetching-data-with-effects
        let cancelled = false;
        (async () => {
            var _a;
            // Switching away from a signed-in identity (to another identity, or to
            // signed-out): detach the old persistor and drop the in-memory cache
            // *before* doing anything else, so the old persistor can never write
            // the previous identity's data into the new identity's key.
            if (restoredFor !== undefined && restoredFor !== null) {
                (_a = persistorRef.current) === null || _a === void 0 ? void 0 : _a.remove();
                persistorRef.current = null;
                await client.cache.reset();
            }
            const currentVersion = window.localStorage.getItem(SCHEMA_VERSION_KEY);
            if (currentVersion !== SCHEMA_VERSION) {
                try {
                    await IDBStorageProvider.removeItem(LEGACY_CACHE_KEY);
                    // Every identity's keyed blob was written under the old schema
                    // too, not just the unkeyed legacy entry — all of them are
                    // incompatible with the new cache shape, so all of them go.
                    const allKeys = await IDBStorageProvider.keys();
                    const staleIdentityKeys = allKeys.filter((key) => typeof key === 'string' && key.startsWith(`${LEGACY_CACHE_KEY}:`));
                    await Promise.all(staleIdentityKeys.map((key) => IDBStorageProvider.removeItem(key)));
                }
                catch (error) {
                    console.error('Error purging legacy cache:', error);
                }
                window.localStorage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
            }
            // A newer identity switch may have started (and already reset the
            // cache) while the code above was awaiting. Bail out here rather than
            // building a persistor for a now-stale identity. `persistor.restore()`
            // below still awaits, so a residual race remains after that point —
            // accepted, since identity transitions are human-speed and the
            // `restoredFor` gate keeps children unmounted until this run finishes.
            if (cancelled)
                return;
            if (identity !== null) {
                const persistor = getCachePersistor(client, identity);
                persistorRef.current = persistor;
                await persistor.restore();
            }
            if (!cancelled) {
                setRestoredFor(identity);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [client, identity, restoredFor]);
    return (React.createElement("div", { className: "h-full" }, restoredFor === identity ? children : fallback));
};
